import React, {
  InputHTMLAttributes,
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  Container,
  Button,
  ButtonNewTraining,
  ContainerButtonsTrainings,
  DeleteSplitButton,
  SplitTab,
} from './styles';
import { useToast } from '../../hooks/toast';
import { Icon } from '../Icon';
import { TrainingsService } from '../../services/TrainingsService';

interface buttonProps extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  value: string;
  userId: string;
  day?: number;
  lastTraining?: number;
  basePath?: string;
  // Chamado depois de apagar o último split com sucesso, pra quem
  // renderiza este componente re-buscar a contagem real de splits e
  // navegar pra longe do split apagado, se for o que está aberto.
  onSplitRemoved?: (removedIndex: number) => void;
}

const SPLIT_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];

const ButtonsSplits: React.FC<buttonProps> = ({
  name,
  day,
  userId,
  lastTraining = 0,
  basePath = '/users',
  onSplitRemoved,
  ...rest
}) => {
  const { addToast } = useToast();
  const trainingsService = new TrainingsService();
  const maxTrainings = 6;

  const [qtdTrainings, setQtdTrainings] = useState(lastTraining);
  const [removing, setRemoving] = useState(false);

  const handleAddTraining = useCallback(() => {
    if (qtdTrainings < maxTrainings) {
      if (lastTraining >= qtdTrainings) {
        setQtdTrainings(qtdTrainings + 1);
      } else {
        addToast({
          type: 'error',
          title: 'Adicione exercícios no último plano de treino criado.',
        });
      }
    } else {
      addToast({
        type: 'error',
        title: 'Quantidade máxima atingida.',
      });
    }
  }, [addToast, lastTraining, qtdTrainings]);

  const handleRemoveSplit = useCallback(
    async (index: number, label: string) => {
      if (removing) return;

      if (
        // eslint-disable-next-line no-alert
        !window.confirm(
          `Apagar o Split ${label}? Todos os exercícios desse split serão perdidos e essa ação não pode ser desfeita.`,
        )
      ) {
        return;
      }

      setRemoving(true);

      try {
        await trainingsService.removeTrainingSplit({
          user_id: userId,
          split_index: index,
        });

        addToast({
          type: 'success',
          title: 'Split apagado com sucesso.',
        });

        setQtdTrainings(index - 1);
        onSplitRemoved?.(index);
      } catch {
        addToast({
          type: 'error',
          title: 'Erro ao apagar o split.',
          description: 'Tente novamente mais tarde.',
        });
      } finally {
        setRemoving(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [addToast, onSplitRemoved, removing, userId],
  );

  useEffect(() => {
    setQtdTrainings(lastTraining);
  }, [lastTraining]);

  const data = SPLIT_LABELS.map((label, index) => ({
    index,
    label: `Split ${label}`,
  }));

  return (
    <Container>
      <ContainerButtonsTrainings>
        {data
          .filter(button => button.index <= qtdTrainings)
          .map(button => {
            // Só o último split REAL (com dado gravado no backend) pode
            // ser apagado -- não usa `qtdTrainings` aqui de propósito,
            // pra não confundir com um split "fantasma" (revelado
            // localmente pelo "+" mas sem nenhum exercício salvo ainda).
            const canDelete = button.index === lastTraining && lastTraining > 0;

            return (
              <SplitTab key={button.index}>
                <Button
                  active={day === button.index}
                  to={`${basePath}/${userId}/${button.index}`}
                >
                  <span>{button.label}</span>
                </Button>

                {canDelete && (
                  <DeleteSplitButton
                    type="button"
                    title="Apagar split"
                    disabled={removing}
                    onClick={() =>
                      handleRemoveSplit(
                        button.index,
                        SPLIT_LABELS[button.index],
                      )
                    }
                  >
                    <Icon name="delete" size={16} color="currentColor" />
                  </DeleteSplitButton>
                )}
              </SplitTab>
            );
          })}
      </ContainerButtonsTrainings>
      <ButtonNewTraining
        type="button"
        title="Adicionar treino"
        onClick={handleAddTraining}
      >
        <Icon name="add_circle" size={22} color="currentColor" />
      </ButtonNewTraining>
    </Container>
  );
};

export { ButtonsSplits };
